import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useScrollReveal } from '../hooks/useScrollReveal';
import type { Product } from '../types';

const Sale = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useScrollReveal();

  useEffect(() => {
    const fetchSaleProducts = async () => {
      try {
        const res = await fetch('/api/products?onSale=true&limit=50');
        const data = await res.json();
        if (data.success) {
          setProducts(data.data.products.filter((p: Product) => p.isActive));
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchSaleProducts();
  }, []);

  // Calculate max discount for the banner
  const maxDiscount = products.reduce((max, p) => {
    if (p.salePrice && p.salePrice > 0) {
      const discount = Math.round(((p.price - p.salePrice) / p.price) * 100);
      return discount > max ? discount : max;
    }
    return max;
  }, 0);

  return (
    <div ref={containerRef} className="min-h-screen bg-offwhite">
      {/* Sale Banner */}
      <section className="bg-offblack text-offwhite pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="section-wide text-center">
          <div className="reveal">
            <span className="inline-block text-xs tracking-[0.3em] uppercase text-sand mb-4">
              Limited Time
            </span>
            <h1 className="font-display text-display-xl tracking-tight mb-4">
              Sale
            </h1>
            {maxDiscount > 0 && (
              <p className="text-lg md:text-xl text-warmgray-400">
                Up to <span className="text-sand font-display font-semibold">{maxDiscount}% off</span> on selected pieces
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 md:py-16">
        <div className="section-wide">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="spinner" />
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-8 reveal">
                <p className="text-sm text-warmgray-500">
                  {products.length} {products.length === 1 ? 'item' : 'items'} on sale
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {products.map((product) => (
                  <div key={product._id} className="reveal">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>

              {/* Browse More */}
              <div className="text-center mt-16 reveal">
                <div className="divider mb-8" />
                <p className="text-warmgray-500 mb-6">Looking for something else?</p>
                <Link to="/collections" className="btn-secondary">
                  Browse Collections
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-24">
              <p className="font-display text-display-sm text-warmgray-400 mb-4">
                No items on sale
              </p>
              <p className="text-warmgray-500 mb-8 max-w-md mx-auto">
                Check back soon — new promotions drop regularly.
              </p>
              <Link to="/collections" className="btn-primary">
                Browse Collections
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Sale;
